import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Layout from "./components/Layout";
import Main from "./pages/main";
import Users from "./pages/users";
import DealsPage from "./pages/deals";
import Login from "./pages/signin";
import ReleasePage from "./pages/release";
import ProductSheetPage from "./pages/product-sheet";
import DocumentsPage from "./pages/documents";
import WebinarPage from "./pages/webinar";
import AcademyPage from "./pages/academy";
import CourseBuilder from "./pages/academy/CourseBuilder";
import ProtectedRoute from "./components/ProtectedRoute";
import SectionRoute from "./components/SectionRoute";
import { landingPathFor } from "./constants/contentSections";
import { useAuth } from "./auth/AuthContext";
import "./App.css";
import GraphicsPage from "./pages/graphics";
import LanguagePage from "./pages/language";
import Item from "./pages/brand-guide/index";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  const location = useLocation();
  const { isAuthenticated, loading, user } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <QueryClientProvider client={queryClient}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
        </div>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to={landingPathFor(user as any)} replace />
            ) : (
              <Login />
            )
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <SectionRoute>
                  <Users />
                </SectionRoute>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                    <Route path="main" element={<SectionRoute><Main /></SectionRoute>} />
                    <Route path="deals" element={<SectionRoute><DealsPage /></SectionRoute>} />
                    <Route
                      path="releases"
                      element={<SectionRoute section="releases"><ReleasePage /></SectionRoute>}
                    />
                    <Route
                      path="product-sheet"
                      element={
                        <SectionRoute section="product-sheets">
                          <ProductSheetPage />
                        </SectionRoute>
                      }
                    />
                    <Route
                      path="documents"
                      element={<SectionRoute section="documents"><DocumentsPage /></SectionRoute>}
                    />
                    <Route
                      path="events"
                      element={<SectionRoute section="events"><WebinarPage /></SectionRoute>}
                    />
                    <Route
                      path="academy"
                      element={<SectionRoute section="academy"><AcademyPage /></SectionRoute>}
                    />
                    <Route
                      path="academy/new"
                      element={<SectionRoute section="academy"><CourseBuilder /></SectionRoute>}
                    />
                    <Route
                      path="academy/:id/edit"
                      element={<SectionRoute section="academy"><CourseBuilder /></SectionRoute>}
                    />
                    <Route
                      path="graphics"
                      element={<SectionRoute section="graphics"><GraphicsPage /></SectionRoute>}
                    />
                    <Route
                      path="language"
                      element={<SectionRoute><LanguagePage /></SectionRoute>}
                    />
                    <Route
                      path="brand-guide"
                      element={<SectionRoute section="brand-guide"><Item /></SectionRoute>}
                    />
                  </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </QueryClientProvider>
  );
}

export default App;
